import axiosInstance from '../utils/axiosInstance';
import { getApiBaseUrl } from '../utils/apiConfig';

// Define API URL
const API_URL = getApiBaseUrl();
console.log('authService API_URL initialized as:', API_URL);

// Function to test server connection
export const testServerConnection = async () => {
  try {
    const response = await axiosInstance.get('/test');
    console.log('Server connection test successful:', response.data);
    return response.data;
  } catch (error) {
    console.error('Server connection test failed:', error);
    throw error;
  }
};

/**
 * Login user
 * @param {Object} credentials - User login credentials (email, password)
 * @returns {Promise} - Promise with the login response
 */
export const login = async (credentials) => {
  try {
    console.log('Login URL:', `${API_URL}/auth/login`);

    const response = await axiosInstance.post('/auth/login', credentials);
    console.log('Login response status:', response.status);

    // Extract token and user data
    const { token, user } = response.data.data;

    // Store authentication data in localStorage
    if (token) {
      localStorage.setItem('token', token);
      if (user) {
        localStorage.setItem('userId', user._id);
      }
      localStorage.setItem('isLoggedIn', 'true');
    }

    return {
      success: true,
      message: 'Login successful',
      data: { token, user }
    };
  } catch (error) {
    console.error('Login error:', error);

    let errorMessage = 'Login failed. Please check your credentials.';
    if (error.response) {
      errorMessage = error.response.data?.error?.message ||
                    error.response.data?.message ||
                    `Server error: ${error.response.status}`;
    } else if (error.request) {
      errorMessage = 'No response from server. Please try again later.';
    } else {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

/**
 * Register new user
 * @param {Object} userData - User registration data
 * @returns {Promise} - Promise with the registration response
 */
export const register = async (userData) => {
  try {
    console.log('Registering user at:', `${API_URL}/auth/register`);
    console.log('With data:', JSON.stringify(userData, null, 2));

    const response = await axiosInstance.post('/auth/register', userData);

    console.log('Registration response status:', response.status);
    console.log('Registration response data:', JSON.stringify(response.data, null, 2));

    // Extract token and user data from response
    const token = response.data?.data?.token;
    const user = response.data?.data?.user;

    // Store authentication data in localStorage
    if (token) {
      localStorage.setItem('token', token);
      if (user) {
        localStorage.setItem('userId', user._id);
        localStorage.setItem('userData', JSON.stringify(user));
      }
      localStorage.setItem('isLoggedIn', 'true');
    }

    return response.data;
  } catch (error) {
    console.error('Registration error:', error);

    let errorMessage = 'Registration failed. Please try again.';
    if (error.response) {
      errorMessage = error.response.data?.error?.message ||
                    error.response.data?.message ||
                    `Server error: ${error.response.status}`;
    } else if (error.request) {
      errorMessage = 'No response from server. Please try again later.';
    } else {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

/**
 * Logout current user
 */
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('userData');
  localStorage.removeItem('userId');
  localStorage.removeItem('isLoggedIn');
};

/**
 * Get current user profile
 * @returns {Promise} - Promise with the current user data
 */
export const getCurrentUser = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    const response = await axiosInstance.get('/auth/me');

    if (response.data && response.data.success) {
      return response.data.data.user;
    }

    return null;
  } catch (error) {
    console.error('Error fetching current user:', error);
    return null;
  }
};

/**
 * Request password reset email
 * @param {string} email - User's email address
 * @returns {Promise} - Promise with the response
 */
export const forgotPassword = async (email) => {
  try {
    const response = await axiosInstance.post('/auth/forgot-password', { email });

    return {
      success: true,
      message: response.data?.message || 'Password reset email sent'
    };
  } catch (error) {
    console.error('Forgot password error:', error);

    let errorMessage = 'Failed to send reset email. Please try again.';
    if (error.response) {
      errorMessage = error.response.data?.error?.message ||
                    error.response.data?.message ||
                    `Server error: ${error.response.status}`;
    } else if (error.request) {
      errorMessage = 'No response from server. Please try again later.';
    } else {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

/**
 * Reset password with token
 * @param {string} token - Password reset token
 * @param {string} newPassword - New password
 * @returns {Promise} - Promise with the response
 */
export const resetPassword = async (token, newPassword) => {
  try {
    const response = await axiosInstance.post('/auth/reset-password', {
      token,
      newPassword
    });

    return {
      success: true,
      message: response.data?.message || 'Password reset successfully'
    };
  } catch (error) {
    console.error('Reset password error:', error);

    let errorMessage = 'Failed to reset password. Please try again.';
    if (error.response) {
      errorMessage = error.response.data?.error?.message ||
                    error.response.data?.message ||
                    `Server error: ${error.response.status}`;
    } else if (error.request) {
      errorMessage = 'No response from server. Please try again later.';
    } else {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
}; 